import { Injectable, inject } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { HttpService } from '../../../core/http/http-service';
import { CompanyContextService } from '../../../core/services/company-context.service';
import {
  CreateIntegrationSettingRequest,
  IntegrationAppDto,
  IntegrationSettingDetailDto,
  IntegrationSettingListItemDto,
  TestConnectionRequest,
  TestConnectionResponse,
  UpdateIntegrationSettingRequest,
} from '../models/settings.models';

@Injectable({ providedIn: 'root' })
export class IntegrationsService {
  private readonly http = inject(HttpService);
  private readonly companyContext = inject(CompanyContextService);

  listApps(): Observable<IntegrationAppDto[]> {
    const companyId = this.resolveCompanyIdOrThrow();
    if (!companyId) {
      return throwError(() => new Error('Şirket seçimi bulunamadı.'));
    }
    return this.http.get<IntegrationAppDto[]>(`/api/companies/${encodeURIComponent(companyId)}/integration-apps`);
  }

  list(): Observable<IntegrationSettingListItemDto[]> {
    const companyId = this.resolveCompanyIdOrThrow();
    if (!companyId) {
      return throwError(() => new Error('Şirket seçimi bulunamadı.'));
    }

    return this.http.get<IntegrationSettingListItemDto[]>(
      `/api/companies/${encodeURIComponent(companyId)}/integration-settings`,
    );
  }

  getDetail(settingPublicId: string): Observable<IntegrationSettingDetailDto> {
    const companyId = this.resolveCompanyIdOrThrow();
    if (!companyId) {
      return throwError(() => new Error('Şirket seçimi bulunamadı.'));
    }

    return this.http.get<IntegrationSettingDetailDto>(
      `/api/companies/${encodeURIComponent(companyId)}/integration-settings/${encodeURIComponent(settingPublicId)}`,
    );
  }

  create(payload: CreateIntegrationSettingRequest): Observable<IntegrationSettingDetailDto> {
    const companyId = this.resolveCompanyIdOrThrow();
    if (!companyId) {
      return throwError(() => new Error('Şirket seçimi bulunamadı.'));
    }

    return this.http.post<IntegrationSettingDetailDto, CreateIntegrationSettingRequest>(
      `/api/companies/${encodeURIComponent(companyId)}/integration-settings`,
      payload,
    );
  }

  update(settingPublicId: string, payload: UpdateIntegrationSettingRequest): Observable<IntegrationSettingDetailDto> {
    const companyId = this.resolveCompanyIdOrThrow();
    if (!companyId) {
      return throwError(() => new Error('Şirket seçimi bulunamadı.'));
    }

    return this.http.put<IntegrationSettingDetailDto, UpdateIntegrationSettingRequest>(
      `/api/companies/${encodeURIComponent(companyId)}/integration-settings/${encodeURIComponent(settingPublicId)}`,
      payload,
    );
  }

  testConnection(payload: TestConnectionRequest): Observable<TestConnectionResponse> {
    const companyId = this.resolveCompanyIdOrThrow();
    if (!companyId) {
      return throwError(() => new Error('Şirket seçimi bulunamadı.'));
    }

    return this.http.post<TestConnectionResponse, TestConnectionRequest>(
      `/api/companies/${encodeURIComponent(companyId)}/integration-settings/test-connection`,
      payload,
    );
  }

  delete(settingPublicId: string): Observable<void> {
    const companyId = this.resolveCompanyIdOrThrow();
    if (!companyId) {
      return throwError(() => new Error('Şirket seçimi bulunamadı.'));
    }

    return this.http.delete<void>(
      `/api/companies/${encodeURIComponent(companyId)}/integration-settings/${encodeURIComponent(settingPublicId)}`,
    );
  }

  private resolveCompanyIdOrThrow(): string | null {
    const companyId = this.companyContext.getSelectedCompanyId();
    return companyId && companyId.trim() ? companyId.trim() : null;
  }
}
